import { array, bool, func, oneOf, string } from 'prop-types'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import ReactSelect, { components } from 'react-select'
import styled from 'styled-components'
const StyledClearIndicator = styled.div`
	align-items: center;
	cursor: pointer;
	display: flex;
	height: 23px;
	justify-content: center;
	width: 23px;
`
const ClearIndicator = props => (
	<StyledClearIndicator onClick={props.clearValue}>
		<svg fill='none' height='16' viewBox='0 0 16 16' width='16'>
			<path
				d='M12 4L4 12'
				stroke='#949494'
				strokeLinecap='round'
				strokeLinejoin='round'
				strokeWidth='2'
			/>
			<path
				d='M4 4L12 12'
				stroke='#949494'
				strokeLinecap='round'
				strokeLinejoin='round'
				strokeWidth='2'
			/>
		</svg>
	</StyledClearIndicator>
)
const Input = props => (
	<components.Input {...props} data-cy={props?.selectProps?.['data-cy']} />
)
const StyledMultiValue = styled.div`
	align-items: center;
	background-color: #f1f1f1;
	border-radius: 8px;
	cursor: default;
	display: flex;
	margin: 4px 4px 0 0;
	max-width: 100%;
	min-height: 38px;
	padding: 4px 7px 4px 10px;
	&[data-disabled='true'] {
		background-color: #ffffff;
		& .label {
			width: 100%;
		}
	}
	& .label {
		font-size: 16px;
		font-weight: 500;
		margin: 0 4px 0 0;
		white-space: pre-wrap;
		width: calc(100% - 25px);
	}
	& .remove {
		align-items: center;
		cursor: pointer;
		display: flex;
		height: 22px;
		justify-content: center;
		width: 22px;
	}
`
const MultiValue = ({
	data: { label },
	isDisabled = false,
	removeProps: { onClick },
}) => (
	<StyledMultiValue data-disabled={isDisabled}>
		<div className='label'>{label}</div>
		{!isDisabled && (
			<div className='remove' onClick={onClick}>
				<svg fill='#949494' height='18' viewBox='0 0 20 20' width='18'>
					<path d='M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z'></path>
				</svg>
			</div>
		)}
	</StyledMultiValue>
)
const IndicatorSeparator = () => null
const Control = props => (
	<components.Control
		{...props}
		innerProps={Object.assign(
			{},
			props?.innerProps,
			props?.selectProps?.isSearchable
				? {}
				: { 'data-cy': props?.selectProps?.['data-cy'] }
		)}
	/>
)
const Option = props => (
	<components.Option
		{...props}
		innerProps={Object.assign({}, props?.innerProps, {
			'data-cy': props?.selectProps?.['data-cy'] + '-' + props?.value,
		})}
	/>
)
const defaultOptions = {
	className: 'multiple-select-input',
	isMulti: true,
	maxMenuHeight: 250,
	menuPlacement: 'auto',
	menuPortalTarget: document.body,
	components: {
		ClearIndicator,
		Control,
		IndicatorSeparator,
		Input,
		MultiValue,
		Option,
	},
}
const styles = {
	valueContainer: styles => ({
		...styles,
		display: 'flex',
		flex: 'initial',
		flexWrap: 'wrap',
		minHeight: 45,
		overflow: 'auto',
		padding: '0 4px 4px 4px',
		width: 'calc(100% - 66px)',
	}),
	placeholder: styles => ({
		...styles,
		color: '#717171',
		fontSize: 16,
		fontWeight: 500,
		height: '100%',
		left: 0,
		margin: 0,
		overflow: 'hidden',
		padding: '10px 0 10px 15px',
		position: 'absolute',
		textOverflow: 'ellipsis',
		top: 0,
		whiteSpace: 'nowrap',
		width: '100%',
		'.dark &': {
			color: '#e1e1e1',
		},
	}),
	input: (styles, { isDisabled, hasValue, selectProps }) => ({
		...styles,
		color: isDisabled ? '#717171' : '#212121',
		fontWeight: 500,
		height: 38,
		margin: `4px 0 0 ${hasValue ? 0 : 11}px`,
		maxWidth: '100%',
		overflow: 'hidden',
		padding: 0,
		visibility: 'visible',
		fontSize:
			selectProps?.size === 'large'
				? 18
				: selectProps?.size === 'small'
				? 14
				: 16,
		':after': {
			maxWidth: '100%',
			overflow: 'hidden',
		},
		'.dark &': {
			color: isDisabled ? '#aaaaaa' : '#e1e1e1',
		},
	}),
	indicatorsContainer: styles => ({
		...styles,
		justifyContent: 'flex-end',
		padding: '0 12px 0 8px',
		width: 66,
	}),
	dropdownIndicator: styles => ({
		...styles,
		alignItems: 'center',
		color: '#949494',
		cursor: 'default',
		display: 'flex',
		height: 23,
		justifyContent: 'center',
		padding: 0,
		width: 23,
		':hover': {
			color: '#949494',
		},
		svg: {
			height: 19,
			width: 19,
		},
		'.dark &': {
			color: '#aaaaaa',
			':hover': {
				color: '#aaaaaa',
			},
		},
	}),
	menuPortal: styles => ({ ...styles, zIndex: 9999 }),
	menu: styles => ({
		...styles,
		backgroundColor: '#ffffff',
		borderRadius: 10,
		border: 'none',
		margin: 0,
		overflow: 'hidden',
		padding: 0,
		boxShadow:
			'0 1px 20px 0 rgba(13, 46, 105, 0.07), 0 1px 20px 0 rgba(13, 46, 105, 0.07)',
		'.dark &': {
			backgroundColor: '#333333',
			boxShadow:
				'0 1px 20px 0px rgba(0, 0, 0, 0.07), 0 1px 20px 0px rgba(0, 0, 0, 0.07)',
		},
	}),
	noOptionsMessage: (styles, props) => ({
		...styles,
		color: 'rgb(37, 42, 59)',
		cursor: 'default',
		fontWeight: 500,
		textAlign: 'left',
		width: '100%',
		fontSize:
			props?.selectProps?.size === 'large'
				? 18
				: props?.selectProps?.size === 'small'
				? 14
				: 16,
		height:
			props?.selectProps?.size === 'large'
				? 52
				: props?.selectProps?.size === 'small'
				? 36
				: 44,
		padding:
			props?.selectProps?.size === 'large'
				? 16
				: props?.selectProps?.size === 'small'
				? 8
				: 12,
		'.dark &': {
			color: '#e1e1e1',
		},
	}),
	menuList: styles => ({
		...styles,
		padding: 5,
		'::-webkit-scrollbar': {
			width: 6,
		},
		'::-webkit-scrollbar-track': {
			backgroundColor: 'transparent',
		},
		'::-webkit-scrollbar-thumb': {
			backgroundColor: '#018ECC',
			borderRadius: 3,
		},
	}),
	option: (styles, { isDisabled, isFocused, selectProps }) => ({
		...styles,
		borderRadius: 10,
		cursor: isDisabled ? 'not-allowed' : 'pointer',
		fontWeight: 500,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		transition: 200,
		whiteSpace: 'nowrap',
		width: '100%',
		fontSize:
			selectProps?.size === 'large'
				? 18
				: selectProps?.size === 'small'
				? 14
				: 16,
		padding:
			selectProps?.size === 'large'
				? '14px 16px'
				: selectProps?.size === 'small'
				? '8px 10px'
				: 12,
		height:
			selectProps?.size === 'large'
				? 52
				: selectProps?.size === 'small'
				? 36
				: 44,
		backgroundColor: isDisabled
			? '#808080'
			: isFocused
			? '#41932133'
			: '#ffffff',
		color: isDisabled
			? '#696f85'
			: isFocused
			? 'rgb(37, 42, 59)'
			: 'rgb(37, 42, 59)',
		':hover': {
			backgroundColor: isDisabled ? '#808080' : '#41932133',
			color: 'rgb(37, 42, 59)',
		},
		'.dark &': {
			color: '#ffffff',
			backgroundColor: isDisabled
				? '#018ECC'
				: isFocused
				? '#41932133'
				: 'transparent',
			':hover': {
				backgroundColor: isDisabled ? '#333333' : '#41932133',
			},
		},
	}),
}
const MultipleSelect = memo(
	({
		'data-cy': dataCY,
		hideBorder = false,
		isDisabled = false,
		isError = false,
		isSearchable = true,
		noOptionsMessage = null,
		onChange,
		onFocus,
		options = [],
		placeholder = '',
		size = 'medium',
		value = [],
	}) => {
		const { t } = useTranslation('components')
		const NoOptionsMessage =
			typeof noOptionsMessage === 'function'
				? noOptionsMessage
				: () => t('no-data')
		return (
			<ReactSelect
				{...defaultOptions}
				data-cy={dataCY}
				isDisabled={isDisabled}
				isSearchable={isSearchable}
				noOptionsMessage={NoOptionsMessage}
				onChange={onChange}
				onFocus={onFocus}
				options={options}
				placeholder={placeholder}
				size={size}
				value={value}
				styles={{
					...styles,
					control: (styles, { isDisabled, isFocused, selectProps }) => ({
						...styles,
						backgroundColor: isDisabled ? '#f4f4f4' : '#ffffff',
						borderRadius: 10,
						borderStyle: 'solid',
						borderWidth: hideBorder ? 0 : 1.5,
						boxShadow: 'none',
						color: '#292b32',
						outline: 'none',
						padding: 0,
						transition: 0,
						width: '100%',
						minHeight:
							selectProps?.size === 'large'
								? 52
								: selectProps?.size === 'small'
								? 36
								: 44,
						cursor: isDisabled ? 'default' : isSearchable ? 'text' : 'pointer',
						borderColor: isFocused
							? '#018ECC'
							: isError
							? '#e41d32'
							: '#e1e1e1',
						':hover': { borderColor: '#018ECC' },
						'.dark &': {
							backgroundColor: '#2E2D2D',
							color: '#e1e1e1',
							borderColor: isFocused
								? '#018ECC'
								: isError
								? '#e41d32'
								: '#555555',
							':hover': { borderColor: '#018ECC' },
						},
					}),
				}}
			/>
		)
	}
)
MultipleSelect.propTypes = {
	'data-cy': string,
	hideBorder: bool,
	isDisabled: bool,
	isError: bool,
	isSearchable: bool,
	noOptionsMessage: func,
	onChange: func,
	onFocus: func,
	options: array,
	placeholder: string,
	size: oneOf(['large', 'medium', 'small']),
	value: array,
}
export default MultipleSelect
